import { Card } from "antd";
import React from "react";

import { DashboardCardExtraContext } from "./DashboardCardExtraContext";

const DRAG_BG = "bg-[#e1eded]";
const IDLE_BG = "bg-white";

export const DashboardCard: React.FC<{
    className?: string;
    title: React.ReactNode;
    subtitle?: string;
    fixedHeight?: number | "auto";
    extra?: React.ReactNode;
    children: React.ReactNode;
    dragging?: boolean;
    scrollBody?: boolean;
}> = ({ className, title, subtitle, fixedHeight = "auto", extra, children, dragging = false, scrollBody = false }) => {
    const [injectedExtra, setInjectedExtra] = React.useState<React.ReactNode>(null);
    const [titleSuffix, setTitleSuffix] = React.useState<React.ReactNode>(null);

    const header = (
        <div className="flex items-center gap-2 w-full min-w-0">
            <div className="min-w-0">
                <h3
                    className={[
                        "m-0 font-semibold text-[20px] leading-6 text-primary",
                        "[&_a]:text-primary [&_a]:underline [&_a]:decoration-1 [&_a]:underline-offset-[3px] [&_a:hover]:no-underline",
                    ].join(" ")}
                >
                    <div className="flex items-baseline gap-2 min-w-0">
                        <span className="truncate">{title}</span>
                        {titleSuffix && <span className="shrink-0 text-neutral-400 font-normal">{titleSuffix}</span>}
                    </div>
                </h3>
                {subtitle && <div className="text-[12px] text-neutral-500 truncate">{subtitle}</div>}
            </div>
        </div>
    );

    return (
        <DashboardCardExtraContext.Provider value={{ setExtra: setInjectedExtra, setTitleSuffix }}>
            <div
                style={{ height: fixedHeight === "auto" ? undefined : fixedHeight }}
                className={[
                    "rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.06)] ring-1 ring-black/5",
                    dragging ? DRAG_BG : IDLE_BG,
                    "transition-colors duration-150 overflow-hidden",
                    className ?? "",
                ].join(" ")}
            >
                <Card
                    bordered={false}
                    title={header}
                    extra={
                        <div className="flex items-center gap-2">
                            {injectedExtra}
                            {extra}
                        </div>
                    }
                    className="!bg-transparent !border-0 !shadow-none h-full flex flex-col p-6"
                    classNames={{
                        header: "shrink-0 h-14 !p-0 bg-transparent !mb-3 !h-auto !min-h-0",
                        body: [
                            "min-h-0 grow !pt-0 bg-transparent transition-colors duration-150 !p-0",
                            scrollBody
                                ? "overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-400/40 hover:scrollbar-thumb-neutral-500/60 [scrollbar-gutter:stable_both-edges]"
                                : "overflow-visible",
                        ].join(" "),
                    }}
                    styles={{ header: { borderBottom: 0 } }}
                    style={{ borderRadius: 10 }}
                >
                    {children}
                </Card>
            </div>
        </DashboardCardExtraContext.Provider>
    );
};
